'use es6';
/*
 * SHAPE
 *
  {
    byId: {
      //id of the file in the code editor as key
      123: {
        parentConnections: {
          //id of the connection as key
          345: {
            connection: {...}
            connectionType: "attached_stylesheets"
            objectType: {
              label: "Layout"
              id: 24
              value: "LAYOUT"
            }
          }
          346: {}
          347: {}
        }
      }
    },
    fetchStatusById: {
      123: PENDING,
      124: SUCCEEDED,
      125: FAILED,
    },
    fetchErrorById: {
      123: null,
      124: null,
      125: new Error(...),
    },
  }
*/

import SeamlessImmutable from 'seamless-immutable';
import { omit } from 'underscore';
import { combineReducers } from 'redux';
import { PENDING, FAILED, SUCCEEDED } from 'DesignData/enums/RequestStatus';
import CosIndexConnection from 'DesignData/models/CosIndexConnection';
import { COS_INDEX_CONNECTIONS_FETCH_PARENTS, COS_INDEX_CONNECTIONS_BULK_FETCH_PARENTS, FINDER_FOLDER_PARENTS_FETCH } from 'DesignData/actions/ActionTypes';

function getAllParents(connections = []) {
  return connections.reduce((parents, connection) => {
    parents.push(new CosIndexConnection(omit(connection, 'parents')));
    return parents.concat(getAllParents(connection.parents));
  }, []);
}

function flattenParents(connections) {
  const parents = getAllParents(connections);
  return parents.reduce((result, parent) => {
    result[parent.model.id] = parent;
    return result;
  }, {});
}

function mergeParents(state, assetId, parents) {
  return state.merge({
    [assetId]: {
      parentConnections: flattenParents(parents)
    }
  });
}
/*
 * byId
 */


function byId(state = SeamlessImmutable({}), action) {
  switch (action.type) {
    case COS_INDEX_CONNECTIONS_FETCH_PARENTS:
      {
        const {
          assetId,
          parents
        } = action;

        if (!parents) {
          return state;
        }

        return mergeParents(state, assetId, parents);
      }

    case COS_INDEX_CONNECTIONS_BULK_FETCH_PARENTS:
      {
        const {
          assetIds,
          parents
        } = action;

        if (!parents) {
          return state;
        }

        return assetIds.reduce((updatedState, assetId) => {
          return mergeParents(updatedState, assetId, parents[assetId]);
        }, state);
      }

    case FINDER_FOLDER_PARENTS_FETCH:
      {
        const {
          files
        } = action;

        if (!files) {
          return state;
        }

        return files.reduce((__state, __file) => {
          const {
            file,
            parents
          } = __file;
          const {
            id: assetId
          } = file;
          return mergeParents(__state, assetId, parents);
        }, state);
      }

    default:
      return state;
  }
}
/*
 * byFolderId
 */


function byFolderId(state = SeamlessImmutable({}), action) {
  switch (action.type) {
    case FINDER_FOLDER_PARENTS_FETCH:
      {
        const {
          folderId,
          files
        } = action;

        if (!files) {
          return state;
        }

        return state.merge({
          [folderId]: files.reduce((result, {
            file
          }) => {
            result.push(file.id);
            return result;
          }, [])
        });
      }

    default:
      return state;
  }
}
/*
 * fetchStatusById
 */


function fetchStatusById(state = SeamlessImmutable({}), action) {
  switch (action.type) {
    case COS_INDEX_CONNECTIONS_FETCH_PARENTS:
      {
        const {
          assetId,
          parents,
          error
        } = action;
        let status;

        if (parents) {
          status = SUCCEEDED;
        } else if (error) {
          status = FAILED;
        } else {
          status = PENDING;
        }

        return state.set(assetId, status);
      }

    case COS_INDEX_CONNECTIONS_BULK_FETCH_PARENTS:
      {
        const {
          assetIds,
          parents,
          error
        } = action;
        let status;

        if (parents) {
          status = SUCCEEDED;
        } else if (error) {
          status = FAILED;
        } else {
          status = PENDING;
        }

        return assetIds.reduce((updatedState, assetId) => {
          return updatedState.set(assetId, status);
        }, state);
      }

    case FINDER_FOLDER_PARENTS_FETCH:
      {
        const {
          folderId,
          files,
          error
        } = action;
        let status;

        if (files) {
          status = SUCCEEDED;
        } else if (error) {
          status = FAILED;
        } else {
          status = PENDING;
        }

        return state.set(folderId, status);
      }

    default:
      {
        return state;
      }
  }
}
/*
 * fetchErrorById
 */


function fetchErrorById(state = {}, action) {
  switch (action.type) {
    case COS_INDEX_CONNECTIONS_FETCH_PARENTS:
      {
        const {
          assetId,
          error = null
        } = action;
        return Object.assign({}, state, {
          [assetId]: error
        });
      }

    case COS_INDEX_CONNECTIONS_BULK_FETCH_PARENTS:
      {
        const {
          assetIds,
          error = null
        } = action;
        return assetIds.reduce((updatedState, assetId) => {
          return Object.assign({}, updatedState, {
            [assetId]: error
          });
        }, state);
      }

    case FINDER_FOLDER_PARENTS_FETCH:
      {
        const {
          folderId,
          error = null
        } = action;
        return Object.assign({}, state, {
          [folderId]: error
        });
      }

    default:
      {
        return state;
      }
  }
}
/*
 * Export
 */


export default combineReducers({
  byId,
  byFolderId,
  fetchStatusById,
  fetchErrorById
});